/**
 * Macro WebSocket Messages
 *
 * Two-message envelope for the stateful macro system. The host owns the walk
 * state and retry budget; the API is a stateless request/reply handler:
 *
 *   host → API   MACRO_WALK_REQUEST   (machine + campaign + trigger + the
 *                                      hashes the host just captured)
 *   API  → host  MACRO_WALK_RESPONSE  (instructions for the next step:
 *                                      either macros to run + captures to
 *                                      take, or a terminal `complete`/
 *                                      `failure` outcome)
 *
 * `captureId` is a nanoid generated server-side per unique (width, height,
 * region) tuple in the next nodes' expectedState; the host echoes it back
 * with the resulting hash, letting the API decode the response without
 * holding walk state.
 *
 * NOTE: This file lives at `macroWalk.ts` rather than `macro.ts` so that
 * `babel-plugin-macros` (used by some downstream apps such as admin-dashboard)
 * does not match the import path against its `/[./]macro(s)?(\.m?js)?$/`
 * regex and try to resolve it as a babel macro. Renaming was the smallest-
 * blast-radius fix; no exported symbols changed.
 *
 * See docs/feature-plans/stateful-macro-system/PHASE_1.md for contracts and
 * docs/feature-plans/stateful-macro-system/EXECUTION_PLAN_v2.md for the full
 * walk algorithm.
 */

import { z } from 'zod';
import { baseMessageSchema, createMessageId } from './_base';
import {
  macroTriggerSchema,
  MACRO_POST_MACRO_DELAY_MAX_MS,
  MACRO_WAIT_INTERVAL_MIN_MS,
  MACRO_WAIT_INTERVAL_MAX_MS,
  MACRO_WAIT_TIMEOUT_MAX_MS,
  macroRegionSchema,
} from '../types/api/macroTree';
import { macroStepErrorCodeSchema } from './macroStepErrors';

export enum MacroMessageType {
  MACRO_WALK_REQUEST = 'MACRO_WALK_REQUEST',
  MACRO_WALK_RESPONSE = 'MACRO_WALK_RESPONSE',
}

// ============================================================================
// SHARED SUB-SCHEMAS
// ============================================================================

/**
 * One captured frame's result. The host returns these in MACRO_WALK_REQUEST;
 * the API specifies the matching specs in MACRO_WALK_RESPONSE. `(width,
 * height)` are echoed back so the API never has to remember which capture it
 * asked for. Optional `region` is omitted when absent or degenerate (full-frame).
 */
const macroCaptureResultSchema = z.object({
  /** Server-generated nanoid that links request → response. */
  captureId: z.string().min(1),
  /** 16-char lowercase hex perceptual hash. */
  hash: z.string().regex(/^[0-9a-f]{16}$/),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  /** Crop in scaled snapshot pixels. Omit for full-frame (legacy hosts). */
  region: macroRegionSchema.optional(),
});
export type MacroCaptureResult = z.infer<typeof macroCaptureResultSchema>;

/**
 * One capture spec the host must request from Playjector before sending the
 * next MACRO_WALK_REQUEST. Optional `region` is omitted when absent or
 * degenerate so Playjector hashes the full scaled frame.
 */
const macroCaptureSpecSchema = z.object({
  captureId: z.string().min(1),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  /** Crop in scaled snapshot pixels. Omit for full-frame. */
  region: macroRegionSchema.optional(),
});
export type MacroCaptureSpec = z.infer<typeof macroCaptureSpecSchema>;

/**
 * Native Playjector `process` / `macroStep` reply body (`body.message`).
 * `errorCode` is optional for host/Playjector version skew.
 */
export const macroStepNativeReplySchema = z.object({
  variantResults: z.record(
    z.string(),
    z.object({
      hash: z.string(),
      width: z.number(),
      height: z.number(),
    }),
  ),
  error: z.string().optional(),
  errorCode: macroStepErrorCodeSchema.optional(),
});
export type MacroStepNativeReply = z.infer<typeof macroStepNativeReplySchema>;

// ============================================================================
// MACRO_WALK_REQUEST  (host → API)
// ============================================================================

/**
 * Bare object shape — kept as a `ZodObject` so it can still be `.merge()`d
 * into the envelope below. Refinements live in `requestInvariants` and are
 * applied to BOTH the payload-level and message-level schemas so that either
 * entry point rejects contradictory payloads.
 */
const macroWalkRequestPayloadBaseSchema = z.object({
  machineId: z.string().min(1),
  campaignId: z.string().min(1),
  trigger: macroTriggerSchema,
  /** Host-owned identity for the current launch attempt. Lets the API tag
   *  request/response logs without owning state. */
  launchInstanceId: z.string().min(1),
  /** The node the host believes it just visited. The API uses this + captures
   *  to decide which child to advance to. `null` means "I just started — give
   *  me the root's macros". */
  currentNodeId: z.string().uuid().nullable(),
  /** Empty array on the very first request (currentNodeId === null). */
  captures: z.array(macroCaptureResultSchema),
  /**
   * Host echo of `waitPoll.matchCurrent === true` (screenshot-node Path Self).
   * Omit on Wait-node / Path Child polls and on non-poll requests.
   */
  waitMatchCurrent: z.boolean().optional(),
});

/**
 * The `macroWalkRequestPayloadBaseSchema` is exported directly as the payload
 * type. The old "captures must be empty when currentNodeId === null" invariant
 * was REMOVED in the unified-graph model: a trigger may have MULTIPLE
 * `kind: 'start'` nodes, and the host sends a captured frame on the initial
 * request so the API can select the closest start (see
 * MacroWalkHandler.initStep). Single-start / legacy hosts still send an empty
 * captures array and are unaffected.
 */
const macroWalkRequestPayloadSchema = macroWalkRequestPayloadBaseSchema;
export type MacroWalkRequestPayload = z.infer<typeof macroWalkRequestPayloadSchema>;

// ============================================================================
// MACRO_WALK_RESPONSE  (API → host)
// ============================================================================

const macroWalkOutcomeSchema = z.enum(['continue', 'complete', 'failure']);
export type MacroWalkOutcome = z.infer<typeof macroWalkOutcomeSchema>;

/** Bare object shape — see note on the request side for why refinements are
 *  not attached here directly. */
const macroWalkResponsePayloadBaseSchema = z.object({
  machineId: z.string().min(1),
  campaignId: z.string().min(1),
  trigger: macroTriggerSchema,
  launchInstanceId: z.string().min(1),
  outcome: macroWalkOutcomeSchema,
  /** Node the host should treat as "next" — same id is echoed back in the
   *  following MACRO_WALK_REQUEST. Null on terminal outcomes. */
  nextNodeId: z.string().uuid().nullable(),
  /** Macros to run before the next capture. For `complete`, these are the
   *  matched leaf node's macros (host runs them, then signals walk done).
   *  Must be empty on `failure`. */
  macros: z.array(z.string()),
  /** What `(captureId, width, height)` tuples the host should request from
   *  Playjector after running `macros`. Must be empty on terminal outcomes
   *  (`complete`/`failure`). */
  captures: z.array(macroCaptureSpecSchema),
  /** Per-node failure behavior pulled from the matched node's `onFailure`.
   *  v1 always 'restart' when present. Null on outcomes that do not require
   *  the host to consult onFailure. */
  onFailure: z.enum(['restart']).nullable(),
  /** Optional human-readable reason for `failure` outcome (e.g. "no match").
   *  Always optional. */
  reason: z.string().optional(),
  /**
   * Milliseconds the host should wait after running `macros` before
   * requesting the next screenshot from Playjector. Sourced from the
   * matched node's `postMacroDelayMs`. Optional for backward compat with
   * old hosts: when absent the host treats it as 0 (no delay). Meaningless
   * on `complete`/`failure` outcomes (no captures requested).
   */
  postMacroDelayMs: z.number().int().min(0).max(MACRO_POST_MACRO_DELAY_MAX_MS).optional(),
  /**
   * Present when the host should poll. Interval/timeout are copied from the
   * Wait node or from a `waitEnabled` node's clocks. Optional so legacy
   * continue payloads stay valid. `matchCurrent` is Path Self only.
   */
  waitPoll: z
    .object({
      intervalMs: z.number().int().min(MACRO_WAIT_INTERVAL_MIN_MS).max(MACRO_WAIT_INTERVAL_MAX_MS),
      timeoutMs: z.number().int().min(MACRO_WAIT_INTERVAL_MIN_MS).max(MACRO_WAIT_TIMEOUT_MAX_MS),
      matchCurrent: z.boolean().optional(),
    })
    .optional(),
});

/** Outcome-specific invariants. The flat shape is preserved (callers and
 *  existing tests build payloads as a single object), but contradictory
 *  combinations are rejected at the schema boundary so coordinators can
 *  trust the shape. */
const responseInvariants = (
  payload: z.infer<typeof macroWalkResponsePayloadBaseSchema>,
  ctx: z.RefinementCtx,
): void => {
  if (payload.outcome === 'continue') {
    if (payload.nextNodeId === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "outcome 'continue' requires a non-null nextNodeId",
        path: ['nextNodeId'],
      });
    }
  } else if (payload.outcome === 'complete') {
    if (payload.nextNodeId !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "outcome 'complete' must have nextNodeId === null",
        path: ['nextNodeId'],
      });
    }
    if (payload.captures.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "outcome 'complete' must have an empty captures array",
        path: ['captures'],
      });
    }
  } else if (payload.outcome === 'failure') {
    if (payload.nextNodeId !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "outcome 'failure' must have nextNodeId === null",
        path: ['nextNodeId'],
      });
    }
    if (payload.macros.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "outcome 'failure' must have an empty macros array",
        path: ['macros'],
      });
    }
    if (payload.captures.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "outcome 'failure' must have an empty captures array",
        path: ['captures'],
      });
    }
  }

  if (payload.waitPoll) {
    if (payload.outcome !== 'continue') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'waitPoll is only valid on outcome continue',
        path: ['waitPoll'],
      });
    }
    if (payload.macros.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'waitPoll requires an empty macros array',
        path: ['macros'],
      });
    }
    if (payload.captures.length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'waitPoll requires a non-empty captures array',
        path: ['captures'],
      });
    }
    if (payload.nextNodeId === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'waitPoll requires a non-null nextNodeId',
        path: ['nextNodeId'],
      });
    }
    if (payload.waitPoll.timeoutMs < payload.waitPoll.intervalMs) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'waitPoll.timeoutMs must be >= waitPoll.intervalMs',
        path: ['waitPoll', 'timeoutMs'],
      });
    }
  }
};

const macroWalkResponsePayloadSchema = macroWalkResponsePayloadBaseSchema.superRefine(responseInvariants);
export type MacroWalkResponsePayload = z.infer<typeof macroWalkResponsePayloadSchema>;

// ============================================================================
// FULL MESSAGE SCHEMAS
// ============================================================================

// `discriminatedUnion` requires `ZodObject` variants, so we merge the BASE
// payload object schemas (no refinements) here. Invariants are then re-applied
// via `.superRefine()` on each variant after the merge so message-level
// validation (e.g. `validators.macro.safeParse(...)`) enforces the same rules.
const macroWalkRequestSchema = baseMessageSchema
  .extend({ type: z.literal(MacroMessageType.MACRO_WALK_REQUEST) })
  .merge(macroWalkRequestPayloadBaseSchema);

const macroWalkResponseSchema = baseMessageSchema
  .extend({ type: z.literal(MacroMessageType.MACRO_WALK_RESPONSE) })
  .merge(macroWalkResponsePayloadBaseSchema);

export const macroMessageSchema = z
  .discriminatedUnion('type', [macroWalkRequestSchema, macroWalkResponseSchema])
  .superRefine((message, ctx) => {
    if (message.type !== MacroMessageType.MACRO_WALK_REQUEST) {
      responseInvariants(message, ctx);
    }
    // Requests have no invariants in the unified-graph model — captures on the
    // initial request are allowed for multi-start selection.
  });
export type MacroMessage = z.infer<typeof macroMessageSchema>;

// ============================================================================
// PAYLOAD TYPE MAPPING + GENERATOR
// ============================================================================

type PayloadType<T extends MacroMessageType> =
  T extends MacroMessageType.MACRO_WALK_REQUEST ? MacroWalkRequestPayload
  : T extends MacroMessageType.MACRO_WALK_RESPONSE ? MacroWalkResponsePayload
  : never;

export function generateMacroMessage<T extends MacroMessageType>(type: T, payload: PayloadType<T>): MacroMessage {
  const base = {
    id: createMessageId(),
    timestamp: Date.now(),
    type,
  };

  // Parse against the refined union so both shape AND outcome-specific
  // invariants are enforced at generation time. Building a malformed message
  // and shipping it would defeat the schema guard added on the consumer side.
  switch (type) {
    case MacroMessageType.MACRO_WALK_REQUEST:
    case MacroMessageType.MACRO_WALK_RESPONSE:
      return macroMessageSchema.parse({ ...base, ...payload });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled macro message type: ${exhaustiveCheck}`);
    }
  }
}
